import React, { useState, useEffect } from 'react';
import { useLanguage } from '../contexts/LanguageContext';
import { useAuth } from '../contexts/AuthContext';
import { Input } from '../components/ui/input';
import { Badge } from '../components/ui/badge';
import { MessageSquare, Search, Phone, Mail, Calendar, RefreshCw, CheckCircle2 } from 'lucide-react';
import { Button } from '../components/ui/button';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL;

function formatDate(iso) {
  if (!iso) return '—';
  return new Date(iso).toLocaleString('ro-RO', {
    day: '2-digit', month: '2-digit', year: 'numeric',
    hour: '2-digit', minute: '2-digit',
  });
}

export default function Feedback() {
  const { language } = useLanguage();
  const { token } = useAuth();
  const isRo = language === 'ro';

  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [resolving, setResolving] = useState(null);

  const fetchFeedback = async () => {
    setLoading(true);
    try {
      const res = await fetch(`${API_URL}/api/admin/feedback`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!res.ok) throw new Error('Eroare la încărcare');
      const data = await res.json();
      setItems(data);
    } catch {
      toast.error(isRo ? 'Eroare la încărcarea feedback-urilor.' : 'Failed to load feedback.');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchFeedback(); }, []);

  const filtered = items.filter((item) => {
    const q = search.toLowerCase();
    return (
      !q ||
      item.user_name?.toLowerCase().includes(q) ||
      item.user_email?.toLowerCase().includes(q) ||
      item.user_phone?.includes(q) ||
      item.message?.toLowerCase().includes(q)
    );
  });

  const handleResolve = async (id) => {
    setResolving(id);
    try {
      const res = await fetch(`${API_URL}/api/admin/feedback/${id}/resolve`, {
        method: 'PATCH',
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!res.ok) throw new Error();
      const data = await res.json();
      setItems(prev => prev.map(i => i.id === id ? { ...i, status: data.status } : i));
    } catch {
      toast.error(isRo ? 'Eroare la actualizarea statusului.' : 'Failed to update status.');
    } finally {
      setResolving(null);
    }
  };

  const newCount = items.filter((i) => i.status === 'new').length;

  return (
    <div className="space-y-6 max-w-4xl">
      {/* Header */}
      <div className="flex items-start justify-between gap-4 flex-wrap">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 rounded-xl bg-primary/10 flex items-center justify-center">
            <MessageSquare className="w-5 h-5 text-primary" />
          </div>
          <div>
            <h1 className="text-2xl font-bold text-foreground">
              {isRo ? 'Feedback utilizatori' : 'User Feedback'}
            </h1>
            <p className="text-sm text-muted-foreground">
              {isRo
                ? `${items.length} mesaje totale${newCount > 0 ? ` · ${newCount} noi` : ''}`
                : `${items.length} total messages${newCount > 0 ? ` · ${newCount} new` : ''}`}
            </p>
          </div>
        </div>
        <Button
          data-testid="feedback-refresh-btn"
          variant="outline"
          size="sm"
          onClick={fetchFeedback}
          disabled={loading}
          className="gap-2"
        >
          <RefreshCw className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`} />
          {isRo ? 'Actualizează' : 'Refresh'}
        </Button>
      </div>

      {/* Search */}
      <div className="relative">
        <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
        <Input
          data-testid="feedback-search"
          placeholder={isRo ? 'Caută după nume, email, telefon sau mesaj...' : 'Search by name, email, phone or message...'}
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          className="pl-9"
        />
      </div>

      {/* Content */}
      {loading ? (
        <div className="flex justify-center py-16">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary" />
        </div>
      ) : filtered.length === 0 ? (
        <div
          data-testid="feedback-empty"
          className="flex flex-col items-center justify-center py-16 text-muted-foreground gap-3"
        >
          <MessageSquare className="w-10 h-10 opacity-30" />
          <p className="text-sm">
            {search
              ? (isRo ? 'Niciun rezultat pentru căutarea ta.' : 'No results for your search.')
              : (isRo ? 'Nu există feedback-uri încă.' : 'No feedback yet.')}
          </p>
        </div>
      ) : (
        <div data-testid="feedback-list" className="space-y-3">
          {filtered.map((item) => (
            <div
              key={item.id}
              data-testid={`feedback-item-${item.id}`}
              className={`rounded-xl border bg-card p-5 transition-all ${
                item.status === 'resolved'
                  ? 'border-green-500/60 bg-green-500/5'
                  : 'border-red-400/60 bg-red-500/5'
              }`}
            >
              {/* Top row */}
              <div className="flex items-start justify-between gap-3 mb-3 flex-wrap">
                <div className="flex items-center gap-2 flex-wrap">
                  <div className="w-8 h-8 rounded-full bg-primary/10 flex items-center justify-center shrink-0">
                    <span className="text-sm font-semibold text-primary">
                      {item.user_name?.charAt(0)?.toUpperCase() || '?'}
                    </span>
                  </div>
                  <div>
                    <span className="font-semibold text-foreground text-sm">{item.user_name}</span>
                    <div className="flex items-center gap-3 mt-0.5 flex-wrap">
                      {item.user_email && (
                        <span className="flex items-center gap-1 text-xs text-muted-foreground">
                          <Mail className="w-3 h-3" />
                          {item.user_email}
                        </span>
                      )}
                      {item.user_phone && (
                        <span className="flex items-center gap-1 text-xs text-muted-foreground">
                          <Phone className="w-3 h-3" />
                          {item.user_phone}
                        </span>
                      )}
                    </div>
                  </div>
                </div>
                <div className="flex items-center gap-2 shrink-0">
                  <span className="flex items-center gap-1 text-xs text-muted-foreground">
                    <Calendar className="w-3 h-3" />
                    {formatDate(item.created_at)}
                  </span>
                  <button
                    disabled={resolving === item.id}
                    onClick={() => handleResolve(item.id)}
                    title={item.status === 'resolved' ? (isRo ? 'Marchează ca nerezolvat' : 'Mark as unresolved') : (isRo ? 'Marchează ca rezolvat' : 'Mark as resolved')}
                    className="rounded-full p-0.5 transition-colors disabled:opacity-50"
                  >
                    <CheckCircle2
                      className={`w-6 h-6 transition-colors ${
                        item.status === 'resolved'
                          ? 'text-green-500'
                          : 'text-muted-foreground/30 hover:text-green-400'
                      }`}
                    />
                  </button>
                </div>
              </div>

              {/* Message */}
              <p
                data-testid={`feedback-message-${item.id}`}
                className="text-sm text-foreground leading-relaxed whitespace-pre-wrap bg-muted/40 rounded-lg px-4 py-3"
              >
                {item.message}
              </p>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
